/**
 * What's Happening Widget - Embeddable Bundle Entry Point
 *
 * Creates a standalone widget embedded in a shadow DOM for style isolation.
 * Same React components as the Next.js dev app, bundled into a single JS file.
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import { ServeFinderPage } from '../src/components/serve-finder/ServeFinderPage';
import { AssetBaseProvider } from '../src/lib/asset-url';
import { PortalContainerProvider } from '@church/nextjs-ui/bottom-sheet';
import styleText from '../src/app/globals.css?inline';

/** Detect the base URL for static assets from the widget script's own origin. */
function detectAssetBase(): string {
  try {
    const scriptSrc = document.currentScript?.getAttribute('src');
    if (scriptSrc) {
      const url = new URL(scriptSrc, window.location.href);
      // Strip `/widget.js` to get the directory (e.g. https://host/whats-happening)
      return url.href.replace(/\/[^/]*$/, '');
    }
  } catch {
    // fallback
  }
  return '';
}

interface WidgetConfig {
  apiBaseUrl?: string;
  containerId?: string;
  featured?: boolean;
}

declare global {
  interface Window {
    WHATS_HAPPENING_WIDGET_CONFIG?: WidgetConfig;
    WhatsHappeningWidget?: WhatsHappeningWidgetInstance;
  }
}

class WhatsHappeningWidgetInstance {
  private root: ReturnType<typeof createRoot> | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private config: WidgetConfig;
  private assetBase: string;

  constructor(config: WidgetConfig = {}, assetBase: string = '') {
    this.config = {
      apiBaseUrl: config.apiBaseUrl || window.location.origin,
      containerId: config.containerId || 'whats-happening-widget-root',
      ...config,
    };
    this.assetBase = assetBase;
  }

  init() {
    const container = document.getElementById(this.config.containerId!);

    if (!container) {
      console.error(`What's Happening Widget: Container #${this.config.containerId} not found`);
      return;
    }

    // Re-use existing shadow root if the widget was already initialized (back/forward nav)
    if (container.shadowRoot) {
      this.shadowRoot = container.shadowRoot;
      const existingApp = this.shadowRoot.getElementById('whats-happening-widget-app');
      if (existingApp) {
        // Already mounted — skip re-init
        console.log("What's Happening Widget: already initialized, skipping");
        return;
      }
    } else {
      this.shadowRoot = container.attachShadow({ mode: 'open' });
    }

    const mountPoint = document.createElement('div');
    mountPoint.id = 'whats-happening-widget-app';
    this.shadowRoot.appendChild(mountPoint);

    // Portal container for Radix UI portals (Dialog/Sheet)
    const portalContainer = document.createElement('div');
    portalContainer.id = 'whats-happening-widget-portals';
    this.shadowRoot.appendChild(portalContainer);

    this.injectFont();
    this.injectStyles();
    this.preventScrollLock();

    (window as any).__WHATS_HAPPENING_WIDGET_PORTAL_CONTAINER__ = portalContainer;

    // Resolve featured flag: config > data-featured attribute > undefined (let component default)
    const featured =
      this.config.featured != null
        ? this.config.featured
        : container.dataset.featured != null
          ? container.dataset.featured === 'true'
          : undefined;

    this.root = createRoot(mountPoint);
    this.root.render(
      <React.StrictMode>
        <PortalContainerProvider container={portalContainer}>
          <AssetBaseProvider value={this.assetBase}>
            <ServeFinderPage featured={featured} />
          </AssetBaseProvider>
        </PortalContainerProvider>
      </React.StrictMode>
    );

    console.log("What's Happening Widget initialized with Shadow DOM");
  }

  /**
   * Prevent Radix UI scroll locking from causing layout shift on the host page.
   * Radix adds data-scroll-locked, overflow:hidden, and paddingRight to body/html
   * when a Dialog/Sheet opens. Since our sheet lives in the shadow DOM, the host
   * page should not be affected.
   */
  private preventScrollLock() {
    if ((window as any).__WHATS_HAPPENING_WIDGET_SCROLL_LOCK_PREVENTED__) return;

    const cleanElement = (el: HTMLElement) => {
      if (el.hasAttribute('data-scroll-locked')) {
        el.removeAttribute('data-scroll-locked');
      }
      // Only remove Radix's specific inline styles, not pre-existing ones
      if (el.style.getPropertyValue('overflow') === 'hidden') {
        el.style.removeProperty('overflow');
      }
      if (el.style.getPropertyValue('padding-right')) {
        el.style.removeProperty('padding-right');
      }
      // Radix also sets margin-right and --removed-body-scroll-bar-size
      if (el.style.getPropertyValue('margin-right')) {
        el.style.removeProperty('margin-right');
      }
    };

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-scroll-locked') {
          cleanElement(document.body);
          cleanElement(document.documentElement);
        }
      }
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-scroll-locked'],
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-scroll-locked'],
    });

    (window as any).__WHATS_HAPPENING_WIDGET_SCROLL_LOCK_PREVENTED__ = true;
  }

  private injectFont() {
    // Google Fonts must be loaded in the document head, not inside shadow DOM
    const fontId = 'whats-happening-widget-montserrat';
    if (!document.getElementById(fontId)) {
      const link = document.createElement('link');
      link.id = fontId;
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap';
      document.head.appendChild(link);
    }
  }

  private injectStyles() {
    if (!this.shadowRoot) return;

    const resetCSS = `
      *, *::before, *::after {
        box-sizing: border-box;
      }

      :host {
        display: block;
        width: 100%;
      }

      #whats-happening-widget-app {
        width: 100%;
        min-width: 100%;
        max-width: 100%;
      }

      :host {
        --tw-border-spacing-x: 0;
        --tw-border-spacing-y: 0;
        --tw-translate-x: 0;
        --tw-translate-y: 0;
        --tw-rotate: 0;
        --tw-skew-x: 0;
        --tw-skew-y: 0;
        --tw-scale-x: 1;
        --tw-scale-y: 1;
        --tw-pan-x: ;
        --tw-pan-y: ;
        --tw-pinch-zoom: ;
        --tw-scroll-snap-strictness: proximity;
        --tw-gradient-from-position: ;
        --tw-gradient-via-position: ;
        --tw-gradient-to-position: ;
        --tw-ordinal: ;
        --tw-slashed-zero: ;
        --tw-numeric-figure: ;
        --tw-numeric-spacing: ;
        --tw-numeric-fraction: ;
        --tw-ring-inset: ;
        --tw-ring-offset-width: 0px;
        --tw-ring-offset-color: #fff;
        --tw-ring-color: rgb(59 130 246 / 0.5);
        --tw-ring-offset-shadow: 0 0 #0000;
        --tw-ring-shadow: 0 0 #0000;
        --tw-shadow: 0 0 #0000;
        --tw-shadow-colored: 0 0 #0000;
        --tw-inset-shadow: 0 0 #0000;
        --tw-inset-ring-shadow: 0 0 #0000;
        --tw-border-style: solid;
        --tw-blur: ;
        --tw-brightness: ;
        --tw-contrast: ;
        --tw-grayscale: ;
        --tw-hue-rotate: ;
        --tw-invert: ;
        --tw-saturate: ;
        --tw-sepia: ;
        --tw-drop-shadow: ;
        --tw-backdrop-blur: ;
        --tw-backdrop-brightness: ;
        --tw-backdrop-contrast: ;
        --tw-backdrop-grayscale: ;
        --tw-backdrop-hue-rotate: ;
        --tw-backdrop-invert: ;
        --tw-backdrop-opacity: ;
        --tw-backdrop-saturate: ;
        --tw-backdrop-sepia: ;
        --tw-contain-size: ;
        --tw-contain-layout: ;
        --tw-contain-paint: ;
        --tw-contain-style: ;
      }
    `;

    // Replace :root → :host and body → #whats-happening-widget-app for shadow DOM
    let shadowCSS = styleText
      .replace(/:root\b/g, ':host')
      .replace(/\bbody\b/g, '#whats-happening-widget-app')
      .replace(/(#whats-happening-widget-app\s*{[^}]*?)overflow-y:\s*scroll\s*!important;?/g, '$1');

    const style = document.createElement('style');
    style.textContent = resetCSS + '\n' + shadowCSS;
    this.shadowRoot.appendChild(style);
  }

  destroy() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }
}

// Auto-initialize — detect asset base before DOMContentLoaded (document.currentScript is only
// available synchronously during script execution, not inside event listeners).
if (typeof window !== 'undefined') {
  const assetBase = detectAssetBase();

  const defaultConfig: WidgetConfig = {
    apiBaseUrl: window.location.origin,
    containerId: 'whats-happening-widget-root',
  };

  const config: WidgetConfig = {
    ...defaultConfig,
    ...(window.WHATS_HAPPENING_WIDGET_CONFIG || {}),
  };

  window.WHATS_HAPPENING_WIDGET_CONFIG = config;

  // Skip if already initialized (e.g. script loaded twice via bfcache)
  if (!window.WhatsHappeningWidget) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        const widget = new WhatsHappeningWidgetInstance(config, assetBase);
        widget.init();
        window.WhatsHappeningWidget = widget;
      });
    } else {
      const widget = new WhatsHappeningWidgetInstance(config, assetBase);
      widget.init();
      window.WhatsHappeningWidget = widget;
    }
  }
}

export default WhatsHappeningWidgetInstance;
